import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { ArrowLeft, User, Trophy, Zap, Target, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/user/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    } finally {
      setLoading(false);
    }
  };

  const achievements = [
    { id: 'first_chapter', title: 'First Steps', description: 'Complete your first chapter', unlocked: (stats?.completed_chapters || 0) >= 1 },
    { id: 'level_complete', title: 'Level Master', description: 'Complete all chapters in one level', unlocked: (stats?.completed_chapters || 0) >= 15 },
    { id: 'game_player', title: 'Game On', description: 'Play your first game', unlocked: (stats?.total_games || 0) >= 1 },
    { id: 'high_scorer', title: 'High Scorer', description: 'Score 1000+ points', unlocked: (stats?.total_score || 0) >= 1000 },
    { id: 'intermediate', title: 'Rising Star', description: 'Reach Intermediate level', unlocked: user?.level !== 'beginner' },
    { id: 'advanced', title: 'Expert Status', description: 'Reach Advanced level', unlocked: user?.level === 'advanced' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          data-testid="back-to-dashboard-button"
          className="mb-8 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-12" data-testid="profile-header">
          <span className="text-xs font-mono uppercase tracking-widest text-primary/80">User Profile</span>
          <h1 className="text-4xl md:text-5xl font-grotesk font-bold text-white mt-2 mb-4 tracking-tight">
            {user?.username}
          </h1>
          <p className="text-zinc-400">{user?.email}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6" data-testid="profile-xp">
            <Zap className="w-5 h-5 text-primary mb-2" />
            <div className="text-3xl font-grotesk font-bold text-white mb-1">{stats?.xp || 0}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide">Total XP</div>
          </div>

          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6" data-testid="profile-level">
            <Target className="w-5 h-5 text-primary mb-2" />
            <div className="text-3xl font-grotesk font-bold text-primary mb-1 uppercase">{stats?.level}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide">Level</div>
          </div>

          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6" data-testid="profile-chapters">
            <Trophy className="w-5 h-5 text-primary mb-2" />
            <div className="text-3xl font-grotesk font-bold text-white mb-1">{stats?.completed_chapters || 0}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide">Chapters Done</div>
          </div>

          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6" data-testid="profile-score">
            <Award className="w-5 h-5 text-primary mb-2" />
            <div className="text-3xl font-grotesk font-bold text-white mb-1">{stats?.total_score || 0}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide">Total Score</div>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-8 mb-8" data-testid="profile-progress">
          <h2 className="text-xl font-grotesk font-semibold text-white mb-6">Learning Progress</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-zinc-400">Overall Completion</span>
                <span className="text-primary font-mono">{Math.round(((stats?.completed_chapters || 0) / 45) * 100)}%</span>
              </div>
              <Progress value={((stats?.completed_chapters || 0) / 45) * 100} className="h-2" />
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div>
          <h2 className="text-2xl font-grotesk font-semibold text-white mb-6">Achievements</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {achievements.map((achievement) => (
              <div
                key={achievement.id}
                data-testid={`achievement-${achievement.id}`}
                className={`bg-zinc-900/30 border rounded-xl p-6 ${
                  achievement.unlocked
                    ? 'border-primary/30 bg-primary/5'
                    : 'border-white/10 opacity-50'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    achievement.unlocked ? 'bg-primary/20' : 'bg-zinc-800'
                  }`}>
                    <Award className={`w-6 h-6 ${
                      achievement.unlocked ? 'text-primary' : 'text-zinc-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-grotesk font-semibold mb-1">{achievement.title}</h3>
                    <p className="text-sm text-zinc-400">{achievement.description}</p>
                    {achievement.unlocked && (
                      <div className="mt-2 text-xs text-primary font-mono uppercase">Unlocked</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
